import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../supabase/supabase';
import { waBilling } from '../utils/support';
import { track } from '../utils/analytics';
import { clearStoredCoupon, getStoredCoupon, normalizeCoupon, storeCoupon } from '../utils/coupon';
import { requestPlan, normalizeTier, TIER_ORDER } from '../utils/subscription';
import { EMPTY_BILLING, loadBillingProfile, saveBillingProfile } from '../utils/billingProfile';
import BillingFields from '../components/BillingFields';

const annualEquivalent = { inicial: '39,17', rotina: '80,83', pro: '205,83' };
const annualTotal = { inicial: '470', rotina: '970', pro: '2.470' };
const monthlyPrice = { inicial: '47', rotina: '97', pro: '247' };

const brl = (minor) => (Number(minor || 0) / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const plans = [
  { id: 'inicial', name: 'Inicial', features: ['1 profissional', 'Até 30 laudos / mês', 'Até 10 fotos por laudo', 'PDF profissional', 'QR Code de validação', 'Suporte básico'] },
  { id: 'rotina', name: 'Rotina', featured: true, features: ['Tudo do Inicial, mais:', 'Até 80 laudos / mês', 'Até 20 fotos por laudo', 'Página pública', 'Duplicar laudo', 'Suporte prioritário'] },
  { id: 'pro', name: 'Pro', features: ['Tudo do Rotina, mais:', 'Até 250 laudos / mês', 'Até 40 fotos por laudo', 'Pastas de organização', 'Laudos favoritos', 'Implantação VIP'] },
];

const Planos = () => {
  const { user, billingState } = useAuth();
  const [searchParams] = useSearchParams();
  const [isAnnual, setIsAnnual] = useState(true);
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState(null);
  const [managing, setManaging] = useState(false);
  const [checkoutTier, setCheckoutTier] = useState(null);
  const [billing, setBilling] = useState(EMPTY_BILLING);
  const [couponInput, setCouponInput] = useState(() => getStoredCoupon() || '');
  const [coupon, setCoupon] = useState(null); // { code, pricing } quando validado pelo Payments Hub
  const [couponError, setCouponError] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const currentTier = normalizeTier(user?.plan_tier);
  const currentIdx = TIER_ORDER.indexOf(currentTier);
  const hasActiveAccess = billingState?.active === true;
  const isTrial = billingState?.source === 'trial';
  const isLocked = billingState && !hasActiveAccess;
  const isPaid = hasActiveAccess && ['payments_hub', 'admin'].includes(billingState?.source);
  const isAdminGranted = billingState?.source === 'admin';
  const activeEntitlement = Array.isArray(billingState?.entitlements)
    ? billingState.entitlements.find((item) => item?.status === 'active' && String(item?.product || '').startsWith(`${currentTier}_`))
    : null;
  const isOneTimeAnnual = String(activeEntitlement?.product || '').endsWith('_annual');
  const isMonthlyRecurring = String(activeEntitlement?.product || '').endsWith('_monthly');
  const selectedPlan = plans.find((plan) => plan.id === checkoutTier);

  useEffect(() => {
    track('pricing_viewed', { surface: 'planos' });
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    loadBillingProfile(user.id).then(setBilling).catch((err) => setError(err.message));
  }, [user?.id]);

  useEffect(() => {
    const tier = searchParams.get('tier');
    const frequency = searchParams.get('frequency');
    if (!['inicial', 'rotina', 'pro'].includes(tier)) return;
    if (frequency === 'mensal' || frequency === 'anual') setIsAnnual(frequency === 'anual');
    setCheckoutTier(tier);
  }, [searchParams]);

  const handleManage = async () => {
    setManaging(true);
    track('subscription_portal_opened', { surface: 'planos' });
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Sessão expirada. Entre novamente.');
      const response = await fetch('/.netlify/functions/billing-portal', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.url) throw new Error(payload.error || 'Não foi possível abrir o gerenciamento da assinatura.');
      window.location.href = payload.url;
    } catch (err) {
      setError(err.message);
      setManaging(false);
    }
  };

  const previewCoupon = async (code, tier = checkoutTier, annual = isAnnual) => {
    const normalized = normalizeCoupon(code);
    setCouponError(null);
    if (!normalized) { setCoupon(null); setCouponError('Cupom inválido.'); return; }
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setCoupon({ code: normalized, pricing: null }); return; } // valida no pagamento, depois do login
    setCouponLoading(true);
    try {
      const response = await fetch('/.netlify/functions/coupon-preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ tier, frequency: annual ? 'anual' : 'mensal', coupon: normalized }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Não foi possível validar o cupom.');
      if (payload.pricing?.applied?.type !== 'coupon') throw new Error('Este cupom não vale para este plano.');
      setCoupon({ code: normalized, pricing: payload.pricing });
      storeCoupon(normalized);
      track('coupon_applied', { plan_tier: tier, frequency: annual ? 'anual' : 'mensal' });
    } catch (err) {
      setCoupon(null);
      setCouponError(err.message);
      track('coupon_rejected', { plan_tier: tier, frequency: annual ? 'anual' : 'mensal' });
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => { setCoupon(null); setCouponInput(''); setCouponError(null); clearStoredCoupon(); };

  useEffect(() => {
    if (!checkoutTier) return;
    const code = coupon?.code || normalizeCoupon(couponInput);
    if (code) previewCoupon(code, checkoutTier, isAnnual);
  }, [checkoutTier, isAnnual]);

  const openCheckout = (tier) => {
    setError(null);
    setCheckoutTier(tier);
  };

  const closeCheckout = () => {
    if (!loading) setCheckoutTier(null);
  };

  const handleSelect = async (tier) => {
    setLoading(tier);
    setError(null);
    try {
      const savedBilling = await saveBillingProfile(user.id, billing);
      setBilling(savedBilling);
      const frequency = isAnnual ? 'anual' : 'mensal';
      const result = await requestPlan({ tier, frequency, billingCustomer: savedBilling, coupon: coupon?.code, expectedValue: coupon?.pricing ? coupon.pricing.final_amount_minor / 100 : undefined });
      switch (result.status) {
        case 'login':
          window.location.href = `/login?redirect=planos&tier=${tier}&frequency=${frequency}`;
          return;
        case 'current':
          setCheckoutTier(null);
          return;
        case 'redirect':
          if (!result.url) throw new Error('Não foi possível abrir o pagamento.');
          window.location.href = result.url;
          return;
        default:
          throw new Error('Não foi possível concluir a operação de pagamento.');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  const submitCheckout = async (event) => {
    event.preventDefault();
    if (checkoutTier) await handleSelect(checkoutTier);
  };

  const planStatusLabel = isAdminGranted
    ? 'Plano concedido pelo administrador'
    : isOneTimeAnnual
      ? 'Plano anual ativo'
      : isMonthlyRecurring
        ? 'Assinatura mensal ativa'
        : 'Plano pago ativo';
  const accessDate = activeEntitlement?.valid_until || null;
  const trialEndDate = billingState?.trial_ends_at ? new Date(billingState.trial_ends_at).toLocaleDateString('pt-BR') : null;

  return (
    <div className="max-w-6xl mx-auto px-4 py-16">
      <div className="text-center mb-12">
        <h1 className="font-display text-4xl font-bold text-navy mb-4 tracking-tight">Escolha seu plano</h1>
        {isTrial ? (
          <p className="text-muted">Você está testando o <span className="text-navy font-semibold uppercase tracking-widest">Rotina</span>{trialEndDate ? ` até ${trialEndDate}` : ''}.</p>
        ) : isLocked ? (
          <p className="text-muted">Seu período de teste terminou. Seus laudos existentes continuam disponíveis; escolha um plano para voltar a criar e editar.</p>
        ) : (
          <p className="text-muted">Você está no plano <span className="text-navy font-semibold uppercase tracking-widest">{currentTier}</span></p>
        )}
        <div className="flex items-center justify-center gap-4 mt-8">
          <span className={`text-sm font-semibold ${!isAnnual ? 'text-ink' : 'text-muted'}`}>Mensal</span>
          <button onClick={() => setIsAnnual((value) => !value)} className={`w-12 h-6 rounded-full p-1 relative border transition-all ${isAnnual ? 'bg-blue-600 border-blue-600' : 'bg-paper-alt border-line'}`} aria-label={isAnnual ? 'Mudar para cobrança mensal' : 'Mudar para cobrança anual'}>
            <motion.div animate={{ x: isAnnual ? 24 : 0 }} className={`w-4 h-4 rounded-full shadow-sm ${isAnnual ? 'bg-white' : 'bg-navy'}`} />
          </button>
          <div className="flex items-center gap-2">
            <span className={`text-sm font-semibold ${isAnnual ? 'text-ink' : 'text-muted'}`}>Anual</span>
            <span className="badge-verified">2 meses grátis</span>
          </div>
        </div>
        {isAnnual && <p className="text-xs text-muted mt-3">12 meses pelo preço de 10, em pagamento único.</p>}
      </div>

      {error && <div className="max-w-md mx-auto mb-8 bg-danger-tint border border-danger/25 text-danger text-sm rounded-card px-4 py-3 text-center">{error}</div>}
      {hasActiveAccess && currentTier === 'pro' && <div className="max-w-2xl mx-auto mb-10 flex items-center gap-3 bg-selo-tint border border-selo/25 text-selo rounded-card px-5 py-4"><p className="text-sm">Você já está no <strong>Pro</strong>, nosso plano mais completo. Precisa de mais volume ou uma estrutura para equipe? <a href={waBilling()} target="_blank" rel="noopener noreferrer" className="underline font-semibold">Fale com o time sobre um plano sob demanda.</a></p></div>}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {plans.map((plan) => {
          const planIdx = TIER_ORDER.indexOf(plan.id);
          const isCurrent = hasActiveAccess && planIdx === currentIdx;
          const isUpgrade = hasActiveAccess && planIdx > currentIdx;
          const isDowngrade = hasActiveAccess && planIdx < currentIdx;
          return <div key={plan.id} className={`relative bg-paper rounded-card p-10 border transition-all flex flex-col ${isCurrent ? 'border-selo ring-4 ring-selo-tint' : plan.featured ? 'border-navy/40 shadow-card' : 'border-line'}`}>
            {isCurrent && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-selo text-white px-4 py-1 rounded-full text-[10px] font-semibold uppercase tracking-widest">{isTrial ? 'Em teste' : 'Seu plano atual'}</div>}
            <div className="mb-8">
              <h3 className="font-display text-2xl font-bold text-ink mb-6">{plan.name}</h3>
              {isAnnual ? <><div className="flex items-baseline gap-1"><span className="text-sm font-semibold text-muted">R$</span><span className="text-5xl font-bold text-ink tracking-tight tabular-nums">{annualEquivalent[plan.id]}</span><span className="text-muted text-sm">/mês</span></div><p className="text-xs text-selo font-semibold mt-2">R$ {annualTotal[plan.id]}/ano</p></> : <div className="flex items-baseline gap-1"><span className="text-sm font-semibold text-muted">R$</span><span className="text-5xl font-bold text-ink tracking-tight tabular-nums">{monthlyPrice[plan.id]}</span><span className="text-muted text-sm">/mês</span></div>}
            </div>
            <div className="space-y-4 mb-10 flex-1">{plan.features.map((feature) => <div key={feature} className={`flex gap-3 text-sm ${feature.includes('Tudo do') ? 'font-semibold text-ink' : 'text-muted'}`}><Check className="w-4 h-4 text-selo shrink-0 mt-0.5" /> {feature}</div>)}</div>
            {isCurrent && !isTrial ? <button disabled className="w-full py-4 rounded-card text-xs font-semibold uppercase tracking-widest bg-paper-alt text-muted border border-line">{isAdminGranted ? 'Concedido pelo admin' : 'Plano atual'}</button> : <button onClick={() => openCheckout(plan.id)} disabled={loading === plan.id} className={`${isDowngrade ? 'btn-secondary' : 'btn-convert'} w-full uppercase tracking-widest text-xs`}>{loading === plan.id ? <Loader2 className="w-4 h-4 animate-spin" /> : isTrial && isCurrent ? 'Assinar Rotina' : isDowngrade ? 'Mudar para este plano' : isUpgrade ? 'Fazer upgrade' : 'Assinar plano'}</button>}
          </div>;
        })}
      </div>

      {isPaid && billingState && <div className="max-w-2xl mx-auto mt-10 rounded-card border border-line bg-paper p-6 shadow-card">
        <div className="flex items-start justify-between gap-4 flex-wrap"><div><h3 className="font-display text-xl font-bold text-ink">{planStatusLabel}</h3>{isAdminGranted ? <p className="text-sm text-muted mt-1">Acesso ativo sem cobrança recorrente e sem vencimento automático.</p> : isOneTimeAnnual && accessDate ? <p className="text-sm text-muted mt-1">Acesso até {new Date(accessDate).toLocaleDateString('pt-BR')}. Não há renovação automática.</p> : accessDate && <p className="text-sm text-muted mt-1">Acesso pago até {new Date(accessDate).toLocaleDateString('pt-BR')}.</p>}</div></div>
        {!isAdminGranted && isMonthlyRecurring && <div className="mt-5 pt-5 border-t border-line text-center"><button onClick={handleManage} disabled={managing} className="text-sm font-medium text-muted hover:text-ink transition-colors min-h-[44px] disabled:opacity-50">{managing ? 'Abrindo…' : 'Gerenciar assinatura'}</button><p className="text-xs text-muted mt-1">Cancelamento, forma de pagamento e faturas.</p></div>}
      </div>}

      <div className="max-w-2xl mx-auto mt-10 flex items-start gap-3 rounded-card border border-line bg-paper-alt px-5 py-4"><p className="text-xs leading-relaxed text-muted">Todos os planos incluem 7 dias de teste. Depois do período de teste, é necessário ter um plano ativo para criar ou editar laudos. Seus laudos já existentes permanecem na sua conta.</p></div>

      {checkoutTier && selectedPlan && <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy/50 px-4 py-6" role="dialog" aria-modal="true" aria-labelledby="billing-title">
        <div className="w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-card bg-paper border border-line shadow-card">
          <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-line bg-paper px-6 py-5"><h2 id="billing-title" className="font-display text-2xl font-bold text-ink">{selectedPlan.name} ({isAnnual ? 'anual' : 'mensal'})</h2><button onClick={closeCheckout} disabled={!!loading} className="p-2 text-muted hover:text-ink" aria-label="Fechar"><X className="w-5 h-5" /></button></div>
          <form onSubmit={submitCheckout} className="p-6 space-y-6">
            <div className="rounded-card border border-line bg-paper-alt p-4"><div><div className="font-semibold text-ink">{isAnnual ? 'Pagamento anual' : 'Assinatura mensal'}</div><div className="text-xs text-muted mt-1">{isAnnual ? 'Pagamento único para 12 meses de acesso, sem renovação automática.' : 'Pagamento recorrente mensal.'}</div></div></div>
            <div className="rounded-card border border-line p-4 space-y-3">
              <label htmlFor="coupon-code" className="block text-xs font-semibold uppercase tracking-widest text-muted">Tem um cupom?</label>
              {coupon ? (
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-ink">Cupom <span className="font-mono">{coupon.code}</span> {coupon.pricing ? 'aplicado' : 'será validado no pagamento'}</div>
                    {coupon.pricing && <div className="mt-1 text-sm"><s className="text-muted">{brl(coupon.pricing.list_amount_minor)}</s> <strong className="text-ink">{brl(coupon.pricing.final_amount_minor)}</strong>{!isAnnual && coupon.pricing.then_minor !== coupon.pricing.first_charge_minor && <span className="text-muted"> na primeira cobrança, depois {brl(coupon.pricing.then_minor)}</span>}{!isAnnual && coupon.pricing.duration === 'repeating' && coupon.pricing.duration_cycles > 1 && <span className="text-muted"> por {coupon.pricing.duration_cycles} meses, depois {brl(coupon.pricing.list_amount_minor)}</span>}{!isAnnual && coupon.pricing.duration === 'forever' && <span className="text-muted"> todo mês</span>}</div>}
                  </div>
                  <button type="button" onClick={removeCoupon} className="text-xs text-muted underline hover:text-ink">Remover</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input id="coupon-code" value={couponInput} onChange={(e) => setCouponInput(e.target.value.toUpperCase())} maxLength={32} autoComplete="off" spellCheck={false} placeholder="Código do cupom" className="flex-1 min-w-0 rounded-card border border-line bg-paper px-3 py-2 text-sm font-mono uppercase" />
                  <button type="button" onClick={() => previewCoupon(couponInput)} disabled={couponLoading || !couponInput.trim()} className="btn-secondary px-4 text-xs disabled:opacity-60">{couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aplicar'}</button>
                </div>
              )}
              {couponError && <p className="text-xs text-danger" role="alert">{couponError}</p>}
            </div>
            <BillingFields billing={billing} setBilling={setBilling} />
            {error && <p className="text-sm text-danger">{error}</p>}
            <button type="submit" disabled={loading === checkoutTier} className="btn-convert w-full min-h-[50px] uppercase tracking-widest text-xs disabled:opacity-60">{loading === checkoutTier ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Continuar para pagamento'}</button>
            <p className="text-[11px] leading-relaxed text-muted text-center">Alterações de plano só são efetivadas após confirmação do pagamento.</p>
          </form>
        </div>
      </div>}
    </div>
  );
};

export default Planos;